import React, { useState, Fragment, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import { withTheme } from 'styled-components'

import { CtaButton, useWindowSize } from 'shared-ui'

// Local primitives
import CloseButton from './primitives/CloseButton'
import NavbarLink from './primitives/NavbarLink'
import BurgerMenu from './primitives/BurgerMenu'
import NavbarLogo from './primitives/NavbarLogo'
import DropDown from './primitives/DropDown'

import {
  Nav,
  BackDrop,
  MenuItemWrapper,
  CloseWrapper,
  GlobalDropdown,
  GlobalItem,
  GlobalInner,
  GlobalTop,
} from './Navbar.styles'

import workforce from '../../images/workforce.png'
import workforceW from '../../images/workforce.webp'
import mentors from '../../images/mentors.png'
import mentorsW from '../../images/mentors.webp'
import top40 from '../../images/top40.png'
import top40W from '../../images/top40.webp'
import blog from '../../images/blog.png'
import blogW from '../../images/blog.webp'
import mimino from '../../images/mimino.png'
import miminoW from '../../images/mimino.webp'
import txtlogo from '../../images/text-logo2.png'
import txtlogoW from '../../images/text-logo2.webp'

const Navbar = ({
  navItems,
  theme,
  globalNav,
  secondaryLogo,
  mobileCTAS,
  isLanding,
  alwaysShowCTA,
}) => {
  const [globalNavState, setGlobalNavState] = useState(false)
  const [sidebarState, setSidebarState] = useState(false)
  const [clickIndex, setClickIndex] = useState(-1)
  const [backdropState, setBackdropState] = useState(false)
  const globalDropDownRef = useRef()
  const [globalDropDownHeight, setGlobalDropDownHeight] = useState(850)
  const size = useWindowSize()
  useEffect(() => {
    globalDropDownRef.current &&
      setGlobalDropDownHeight(
        globalDropDownRef.current.getBoundingClientRect().height > 350
          ? globalDropDownRef.current.getBoundingClientRect().height
          : 850
      )

    const handleGlobalDropDownResize = () => {
      globalDropDownRef.current &&
        setGlobalDropDownHeight(
          globalDropDownRef.current.getBoundingClientRect().height > 350
            ? globalDropDownRef.current.getBoundingClientRect().height
            : 850
        )
    }

    window.addEventListener('resize', handleGlobalDropDownResize)
    handleGlobalDropDownResize()
    return () =>
      window.removeEventListener('resize', handleGlobalDropDownResize)
  }, [size])

  const breakpoint = theme.breakpoints.nav

  const handleSidebar = () => {
    if (sidebarState) {
      setSidebarState(false)
      setBackdropState(false)
      document.body.style.overflowY = 'scroll'
    } else {
      setSidebarState(true)
      setBackdropState(true)
      setGlobalNavState(false)
      document.body.style.overflowY = 'hidden'
    }
  }
  const handleClick = (index) => {
    if (clickIndex === index && size.width > breakpoint) {
      document.body.style.overflowY = 'scroll'
      setClickIndex(-1)
      setBackdropState(false)
    } else if (index === -1) {
      document.body.style.overflowY = 'scroll'
      setClickIndex(-1)
      setBackdropState(false)
    } else if (clickIndex === index && size.width <= breakpoint) {
      setClickIndex(-1)
    } else {
      setGlobalNavState(false)
      setClickIndex(index)
      setBackdropState(true)

      document.body.style.overflowY = 'hidden'
    }
  }

  const handleBackdrop = () => {
    setBackdropState(!backdropState)
    setSidebarState(false)
    setGlobalNavState(false)
    setClickIndex(-1)
    document.body.style.overflowY = 'scroll'
  }

  const handleGlobalNav = () => {
    setGlobalNavState(!globalNavState)
    if (!backdropState && clickIndex === -1) {
      setBackdropState(true)
      document.body.style.overflowY = 'hidden'
    } else if (clickIndex !== -1) {
      setClickIndex(-1)
    } else {
      handleBackdrop()
    }
  }

  return (
    <>
      <GlobalDropdown
        globalNavState={globalNavState}
        ref={globalDropDownRef}
        position={globalDropDownHeight}
        clickIndex={clickIndex !== -1}
        sidebarState={sidebarState}
      >
        <GlobalTop>
          <a
            href="https://promazo.com"
            aria-label="Click to be routed back to the ProMazo landing"
          >
            <picture>
              <source srcSet={txtlogoW} type="image/webp" />
              <source srcSet={txtlogo} type="image/jpeg" />
              <img src={txtlogo} alt="ProMazo, route to landing page" />
            </picture>
          </a>
          <div>
            {!isLanding && <a href="https://promazo.com">Visit ProMazo</a>}
            <div
              role="button"
              tabIndex={0}
              onClick={handleGlobalNav}
              onKeyDown={handleGlobalNav}
            >
              <CloseButton />
            </div>
          </div>
        </GlobalTop>

        <GlobalInner>
          {globalNav.map((item, i) => (
            <GlobalItem
              key={`global-nav-${i}`}
              big={item.big}
              float={item.float}
              globalNavState={globalNavState}
            >
              <div className="fade" style={{ transitionDelay: `${90 * i}ms` }}>
                <a
                  href={item.link}
                  style={{ overflow: 'visible' }}
                  aria-label={item.alt}
                >
                  <picture>
                    <source srcSet={item.img.srcWebp} type="image/webp" />
                    <source srcSet={item.img.src} type="image/jpeg" />
                    <img src={item.img.src} alt={item.img.alt} />
                  </picture>
                  <p>{item.txt}</p>
                </a>
              </div>
            </GlobalItem>
          ))}
        </GlobalInner>
      </GlobalDropdown>
      <Nav
        globalNavState={globalNavState}
        sidebarState={sidebarState}
        clickIndex={clickIndex !== -1}
        alwaysShowCTA={alwaysShowCTA}
      >
        <NavbarLogo
          handleGlobalNav={handleGlobalNav}
          secondaryLogo={secondaryLogo}
          active={globalNavState}
        />
        {navItems && <BurgerMenu handleNavbar={handleSidebar} />}

        <ul className={sidebarState ? 'active' : ''}>
          <CloseWrapper onClick={handleSidebar} className="close">
            <CloseButton />
          </CloseWrapper>

          {navItems &&
            navItems.map((nav, i) => {
              let isDesktopDropDown = nav.dropDownItems !== undefined
              return (
                <Fragment key={`nav-item-${i}`}>
                  {isDesktopDropDown ? (
                    <MenuItemWrapper onClick={() => handleClick(i)}>
                      <NavbarLink
                        show={i === clickIndex}
                        slug={nav.slug}
                        text={nav.text}
                      />
                    </MenuItemWrapper>
                  ) : (
                    <MenuItemWrapper>
                      <NavbarLink text={nav.text} slug={nav.slug} />
                    </MenuItemWrapper>
                  )}

                  {i === clickIndex && (
                    <DropDown
                      dropDownItems={nav.dropDownItems}
                      button={nav.button}
                    />
                  )}
                </Fragment>
              )
            })}
          {!!mobileCTAS &&
            mobileCTAS.map((cta, i) => (
              <li className="cta" key={`nav-mobile-cta-${i}`}>
                <CtaButton
                  secondary={i === 1}
                  {...cta}
                  size={
                    size !== 'undefined' ? (size.width > breakpoint ? 1 : 2) : 1
                  }
                >
                  {cta.text}
                </CtaButton>
              </li>
            ))}
        </ul>
      </Nav>

      <BackDrop
        onClick={handleBackdrop}
        backDropState={backdropState}
        height={size.height}
        globalNavState={globalNavState}
      />
    </>
  )
}

Navbar.defaultProps = {
  navItems: null,
  mobileCTAS: null,
  globalNav: [
    {
      txt: 'High Quality, Flexible, and Diverse Work Teams',
      big: true,
      link: 'https://workforce.promazo.com/',
      img: {
        srcWebp: workforceW,
        src: workforce,
        alt: 'Workforce logo and link to site',
      },
    },
    {
      txt: 'Personalized Digital Career Center',
      big: true,
      link: 'https://mimino.promazo.com/',
      img: {
        srcWebp: miminoW,
        src: mimino,
        alt: 'Mimino logo and link to site',
      },
    },
    {
      txt: 'Guide the Next Generation',
      big: true,
      link: 'https://mentors.promazo.com/',
      img: {
        srcWebp: mentorsW,
        src: mentors,
        alt: 'Mentors logo and link to site',
      },
    },
    {
      txt: 'Top 40 Students',
      link: 'https://students.promazo.com/',
      float: 'left',
      img: {
        srcWebp: top40W,
        src: top40,
        alt: 'Top 40 graphic and link to site',
      },
    },
    {
      txt: 'Our Blog',
      link: 'https://medium.com/promazo',
      float: 'right',
      img: {
        srcWebp: blogW,
        src: blog,
        alt: 'Blog graphic and link to medium site',
      },
    },
  ],
}

Navbar.propTypes = {
  navItems: PropTypes.array,
  globalNav: PropTypes.array,
  mobileCTAS: PropTypes.array,
}

export default withTheme(Navbar)
